"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "@/components/Link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectCoverflow, Keyboard } from "swiper/modules";
import type SwiperCore from "swiper";
import { AppRoutes } from "@/lib/routeResolver";
import { getCountryFlag } from "@/lib/countryFlags";
import type { ExperienceDestinationCard } from "@/lib/navigation";

import "swiper/css";
import "swiper/css/effect-coverflow";
import styles from "./experience-destinations-slider.module.css";

const FALLBACK_IMG = "/main/img/tour/tour1.jpg";
const SIZES = "(max-width: 575px) 78vw, (max-width: 991px) 42vw, 286px";

export default function ExperienceDestinationsSlider({
  destinations,
  title = "Destinations with experiences",
  eyebrow = "Where we run tours",
  excludeSlug,
}: {
  destinations: ExperienceDestinationCard[];
  title?: string;
  eyebrow?: string;
  excludeSlug?: string;
}) {
  const swiperRef = useRef<SwiperCore | null>(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const items = excludeSlug
    ? destinations.filter((d) => d.slug !== excludeSlug)
    : destinations;

  if (items.length === 0) return null;

  return (
    <section className={styles.section} aria-labelledby="experience-destinations">
      <div className="container">
        <div className={styles.head}>
          <span className={styles.eyebrow}>{eyebrow}</span>
          <h2 className={styles.title} id="experience-destinations">
            {title}
          </h2>
        </div>

        <div className={styles.deck}>
          <Swiper
            className={styles.slider}
            modules={[Autoplay, EffectCoverflow, Keyboard]}
            effect="coverflow"
            coverflowEffect={{
              rotate: 2,
              stretch: 12,
              depth: 120,
              modifier: 1.35,
              scale: 0.88,
              slideShadows: false,
            }}
            centeredSlides
            slidesPerView="auto"
            spaceBetween={22}
            loop={items.length > 3}
            grabCursor
            keyboard={{ enabled: true }}
            speed={700}
            autoplay={
              reduceMotion
                ? false
                : {
                    delay: 4200,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
            }
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
          >
            {items.map((dest) => (
              <SwiperSlide key={dest.id} className={styles.slide}>
                <Link
                  href={AppRoutes.experienceDestination(dest.slug)}
                  className={styles.card}
                  title={`${dest.name} experiences and guided tours`}
                >
                  <Image
                    src={(dest.coverPhoto || FALLBACK_IMG).replace(/ /g, "%20")}
                    alt={`${dest.name} experiences`}
                    fill
                    sizes={SIZES}
                    className={styles.img}
                  />
                  <div className={styles.scrim} aria-hidden="true" />
                  <div className={styles.frame} aria-hidden="true" />

                  <div className={styles.flag} aria-hidden="true">
                    <img
                      src={getCountryFlag(dest.name)}
                      alt=""
                      width={24}
                      height={24}
                      loading="lazy"
                      decoding="async"
                      className={styles.flagImg}
                      onError={(e) => {
                        e.currentTarget.style.visibility = "hidden";
                      }}
                    />
                  </div>

                  <div className={styles.content}>
                    <div className={styles.rule} aria-hidden="true" />
                    <span className={styles.region}>{dest.regionName}</span>
                    <div className={styles.nameRow}>
                      <h3 className={styles.name}>{dest.name}</h3>
                      <svg
                        className={styles.go}
                        width="26"
                        height="10"
                        viewBox="0 0 26 10"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M0 5h24M20 1l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1"
                        />
                      </svg>
                    </div>
                    {dest.tourCount > 0 && (
                      <span className={styles.count}>
                        {dest.tourCount} experience
                        {dest.tourCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className={styles.controls}>
            <button
              type="button"
              className={`${styles.nav} ${styles.prev}`}
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Previous destination"
            >
              <svg width="16" height="14" viewBox="0 0 16 14" fill="none" aria-hidden="true">
                <path d="M15 7H1M5 1L1 7l4 6" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </button>
            <button
              type="button"
              className={`${styles.nav} ${styles.next}`}
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Next destination"
            >
              <svg width="16" height="14" viewBox="0 0 16 14" fill="none" aria-hidden="true">
                <path d="M1 7h14M11 1l4 6-4 6" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
